import {
  deepEqualJson,
  type StockCommandEnvelopeV1,
} from '@trackeat/shared/sync';

const STOCK_COMMAND_WRITEBACK_SCHEMA = 'trackeat.stock-command-writeback';
const STOCK_COMMAND_WRITEBACK_VERSION = 1;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HASH_PATTERN = /^[0-9a-f]{64}$/;
const TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/;

const RESULT_KEYS = [
  'schema',
  'version',
  'tenant_id',
  'command_id',
  'command_digest',
  'event_count',
  'first_server_rev',
  'last_server_rev',
  'writeback',
  'master_effects',
  'replayed',
] as const;

const WRITEBACK_KEYS = [
  'events',
  'audit_events',
  'foods',
  'shipments',
  'masters',
] as const;

const EVENT_KEYS = [
  'ordinal',
  'id',
  'tenant_id',
  'business_id',
  'kind',
  'actor_user_id',
  'role',
  'device_id',
  'client_uuid',
  'client_ts',
  'server_ts',
  'server_rev',
  'payload',
  'prev_hash',
  'self_hash',
  'hash_version',
  'anchor_id',
] as const;

const AUDIT_EVENT_KEYS = EVENT_KEYS.filter((key) => key !== 'ordinal');
const MASTER_KEYS = ['table', 'id', 'version', 'row'] as const;

type JsonRecord = Record<string, unknown>;

export interface StockCommandAckValidationInput {
  envelope: StockCommandEnvelopeV1;
  result: unknown;
  downstream?: StockCommandAckDownstreamContextV1;
}

export interface StockCommandAckDownstreamContextV1 {
  kind: 'shipment_void';
  shipment_id: string;
}

/**
 * A StockCommand ACK is an authorization boundary, not merely a syntactic RPC
 * response. This validator binds every local write to the exact envelope that
 * was sent before SQLite is allowed to apply the server writeback.
 *
 * Downstream V1 allowlist:
 * - `foods`: only rows the stock_in triggers are documented to mutate;
 * - `shipments`: the command-bound issued shipment, or the shipment atomically
 *   voided by a reverse wrapper;
 * - `audit_events`: the matching shipment_dispatched/shipment_voided fact;
 * - `masters`: exactly the targets declared by envelope.master_effects.
 */
export function validateStockCommandAckWritebackV1({
  envelope,
  result,
  downstream,
}: StockCommandAckValidationInput): JsonRecord {
  const ack = requireRecord(result, 'stock_command_ack_result_invalid');
  requireExactKeys(ack, RESULT_KEYS, 'stock_command_ack_result_shape_invalid');

  if (ack.schema !== STOCK_COMMAND_WRITEBACK_SCHEMA) {
    fail('stock_command_ack_schema_mismatch');
  }
  if (ack.version !== STOCK_COMMAND_WRITEBACK_VERSION) {
    fail('stock_command_ack_version_mismatch');
  }
  if (ack.tenant_id !== envelope.command.tenant_id) {
    fail('stock_command_ack_tenant_mismatch');
  }
  if (ack.command_id !== envelope.command.command_id) {
    fail('stock_command_ack_command_id_mismatch');
  }
  if (ack.command_digest !== envelope.command_digest) {
    fail('stock_command_ack_command_digest_mismatch');
  }
  if (ack.event_count !== envelope.events.length) {
    fail('stock_command_ack_event_count_mismatch');
  }
  if (typeof ack.replayed !== 'boolean') {
    fail('stock_command_ack_replayed_invalid');
  }

  const writeback = requireRecord(
    ack.writeback,
    'stock_command_ack_writeback_invalid',
  );
  requireExactKeys(
    writeback,
    WRITEBACK_KEYS,
    'stock_command_ack_writeback_shape_invalid',
  );
  const events = requireArray(
    writeback.events,
    'stock_command_ack_events_invalid',
  );
  const auditEvents = requireArray(
    writeback.audit_events,
    'stock_command_ack_audit_events_invalid',
  );
  const foods = requireArray(
    writeback.foods,
    'stock_command_ack_foods_invalid',
  );
  const shipments = requireArray(
    writeback.shipments,
    'stock_command_ack_shipments_invalid',
  );
  const masters = requireArray(
    writeback.masters,
    'stock_command_ack_masters_invalid',
  );
  const mirroredMasters = requireArray(
    ack.master_effects,
    'stock_command_ack_master_effects_invalid',
  );

  if (!deepEqualJson(mirroredMasters, masters)) {
    fail('stock_command_ack_master_effects_mismatch');
  }

  const validatedEvents = validateCommandEvents(envelope, events);
  validateRevisionBounds(ack, validatedEvents);
  const shipmentTarget = commandShipmentTarget(envelope, downstream);
  const validatedShipments = validateShipments(
    envelope,
    shipments,
    shipmentTarget,
  );
  const validatedAudits = validateAuditEvents(
    envelope,
    auditEvents,
    validatedEvents,
    validatedShipments,
    shipmentTarget,
  );
  validateEventChain(validatedEvents, validatedAudits);
  validateFoods(envelope, foods);
  validateMasters(envelope, masters);

  return ack;
}

function fail(code: string, detail?: string): never {
  throw new Error(detail ? `${code}:${detail}` : code);
}

function isRecord(value: unknown): value is JsonRecord {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function requireRecord(value: unknown, code: string): JsonRecord {
  if (!isRecord(value)) fail(code);
  return value;
}

function requireArray(value: unknown, code: string): unknown[] {
  if (!Array.isArray(value)) fail(code);
  return value;
}

function requireExactKeys(
  value: JsonRecord,
  expected: readonly string[],
  code: string,
): void {
  const expectedKeys = new Set(expected);
  const actualKeys = Object.keys(value);
  const unexpected = actualKeys.find((key) => !expectedKeys.has(key));
  if (unexpected) fail(code, `unexpected_${unexpected}`);
  const missing = expected.find((key) => !Object.prototype.hasOwnProperty.call(value, key));
  if (missing) fail(code, `missing_${missing}`);
}

function requireUuid(value: unknown, code: string): string {
  if (typeof value !== 'string' || !UUID_PATTERN.test(value)) fail(code);
  return value;
}

function requirePositiveInteger(value: unknown, code: string): number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) fail(code);
  return value as number;
}

function requireNonNegativeInteger(value: unknown, code: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) fail(code);
  return value as number;
}

function requireHash(value: unknown, code: string): string {
  if (typeof value !== 'string' || !HASH_PATTERN.test(value)) fail(code);
  return value;
}

function requireTimestamp(value: unknown, code: string): string {
  if (
    typeof value !== 'string'
    || value.length === 0
    || !TIMESTAMP_PATTERN.test(value)
    || !Number.isFinite(Date.parse(value))
  ) {
    fail(code);
  }
  return value;
}

function sameInstant(left: string, right: string): boolean {
  return Date.parse(left) === Date.parse(right);
}

function normalizedWireJson(value: unknown, code: string): unknown {
  try {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) fail(code);
    return JSON.parse(serialized) as unknown;
  } catch {
    fail(code);
  }
}

interface ValidatedServerEvent {
  row: JsonRecord;
  clientUuid: string;
  serverRev: number;
  prevHash: string;
  selfHash: string;
}

function validateServerEventShape(
  value: unknown,
  keys: readonly string[],
  codePrefix: string,
): ValidatedServerEvent {
  const row = requireRecord(value, `${codePrefix}_invalid`);
  requireExactKeys(row, keys, `${codePrefix}_shape_invalid`);
  requireUuid(row.id, `${codePrefix}_id_invalid`);
  requireUuid(row.tenant_id, `${codePrefix}_tenant_invalid`);
  if (row.business_id !== null) {
    requireUuid(row.business_id, `${codePrefix}_business_invalid`);
  }
  if (typeof row.kind !== 'string' || row.kind.length === 0) {
    fail(`${codePrefix}_kind_invalid`);
  }
  requireUuid(row.actor_user_id, `${codePrefix}_actor_invalid`);
  if (typeof row.role !== 'string' || row.role.length === 0) {
    fail(`${codePrefix}_role_invalid`);
  }
  if (typeof row.device_id !== 'string' || row.device_id.length === 0) {
    fail(`${codePrefix}_device_invalid`);
  }
  const clientUuid = requireUuid(
    row.client_uuid,
    `${codePrefix}_client_uuid_invalid`,
  );
  requireTimestamp(row.client_ts, `${codePrefix}_client_ts_invalid`);
  requireTimestamp(row.server_ts, `${codePrefix}_server_ts_invalid`);
  const serverRev = requirePositiveInteger(
    row.server_rev,
    `${codePrefix}_server_rev_invalid`,
  );
  requireRecord(row.payload, `${codePrefix}_payload_invalid`);
  const prevHash = requireHash(row.prev_hash, `${codePrefix}_prev_hash_invalid`);
  const selfHash = requireHash(row.self_hash, `${codePrefix}_self_hash_invalid`);
  requirePositiveInteger(row.hash_version, `${codePrefix}_hash_version_invalid`);
  if (row.anchor_id !== null) {
    requireUuid(row.anchor_id, `${codePrefix}_anchor_invalid`);
  }
  return {
    row,
    clientUuid,
    serverRev,
    prevHash,
    selfHash,
  };
}

function validateCommandEvents(
  envelope: StockCommandEnvelopeV1,
  values: unknown[],
): ValidatedServerEvent[] {
  const expectedByClientUuid = new Map(
    envelope.events.map((event, index) => {
      if (envelope.events.some(
        (candidate, candidateIndex) => (
          candidateIndex < index && candidate.client_uuid === event.client_uuid
        ),
      )) {
        fail('stock_command_ack_expected_event_duplicate', event.client_uuid);
      }
      return [event.client_uuid, { event, index }] as const;
    }),
  );
  const seen = new Set<string>();
  const validated = values.map((value, index) => {
    const current = validateServerEventShape(
      value,
      EVENT_KEYS,
      'stock_command_ack_event',
    );
    if (seen.has(current.clientUuid)) {
      fail('stock_command_ack_event_duplicate', current.clientUuid);
    }
    seen.add(current.clientUuid);
    const expected = expectedByClientUuid.get(current.clientUuid);
    if (!expected) {
      fail('stock_command_ack_event_extra', current.clientUuid);
    }
    if (index !== expected.index || current.row.ordinal !== expected.index + 1) {
      fail('stock_command_ack_event_ordinal_mismatch', current.clientUuid);
    }
    const expectedSeq = expected.event.payload.command_seq;
    const returnedPayload = requireRecord(
      current.row.payload,
      'stock_command_ack_event_payload_invalid',
    );
    if (
      expectedSeq !== expected.index + 1
      || returnedPayload.command_seq !== expectedSeq
    ) {
      fail('stock_command_ack_event_seq_mismatch', current.clientUuid);
    }
    if (current.row.tenant_id !== expected.event.tenant_id) {
      fail('stock_command_ack_event_tenant_mismatch', current.clientUuid);
    }
    if (current.row.business_id !== expected.event.business_id) {
      fail('stock_command_ack_event_business_mismatch', current.clientUuid);
    }
    if (current.row.kind !== expected.event.kind) {
      fail('stock_command_ack_event_kind_mismatch', current.clientUuid);
    }
    if (current.row.device_id !== expected.event.device_id) {
      fail('stock_command_ack_event_device_mismatch', current.clientUuid);
    }
    if (!sameInstant(
      current.row.client_ts as string,
      expected.event.client_ts,
    )) {
      fail('stock_command_ack_event_client_ts_mismatch', current.clientUuid);
    }
    const expectedPayload = normalizedWireJson(
      expected.event.payload,
      'stock_command_ack_expected_event_payload_invalid',
    );
    // The canonical SQL writer normalizes only this audit field using numeric
    // multiplication (T-486). Bind that exact decimal result to the source
    // command; never accept a generic epsilon on quantities or money.
    let comparablePayload = current.row.payload;
    if (envelope.command.movement_class === 'uom_rebase'
      && expected.event.kind === 'stock_out') {
      const line = envelope.command.lines[expected.index];
      if (line && !('zero_balance' in line)
        && returnedPayload.rebase_target_qty === decimalProduct(line.source_qty, line.conversion.factor)) {
        comparablePayload = {
          ...returnedPayload,
          rebase_target_qty: (expectedPayload as JsonRecord).rebase_target_qty,
        };
      }
    }
    // The SQL writer signs shared-tablet facts after validating the command.
    // Keep that signature in the writeback/hash chain, but compare the domain
    // payload using the same bounded signature contract as label audits.
    if (!serverSignedPayloadMatches(comparablePayload, expectedPayload as JsonRecord)) {
      fail('stock_command_ack_event_payload_mismatch', current.clientUuid);
    }
    return current;
  });

  for (const clientUuid of expectedByClientUuid.keys()) {
    if (!seen.has(clientUuid)) {
      fail('stock_command_ack_event_missing', clientUuid);
    }
  }
  if (values.length !== envelope.events.length) {
    fail('stock_command_ack_event_cardinality_mismatch');
  }
  return validated;
}

function validateRevisionBounds(
  ack: JsonRecord,
  events: readonly ValidatedServerEvent[],
): void {
  if (events.length === 0) {
    if (ack.first_server_rev !== null || ack.last_server_rev !== null) {
      fail('stock_command_ack_revision_bounds_mismatch');
    }
    return;
  }
  if (
    ack.first_server_rev !== events[0].serverRev
    || ack.last_server_rev !== events[events.length - 1].serverRev
  ) {
    fail('stock_command_ack_revision_bounds_mismatch');
  }
}

function validateEventChain(
  events: readonly ValidatedServerEvent[],
  auditEvents: readonly ValidatedServerEvent[],
): void {
  const chain = [...events, ...auditEvents];
  for (let index = 1; index < chain.length; index += 1) {
    const previous = chain[index - 1];
    const current = chain[index];
    if (
      current.serverRev !== previous.serverRev + 1
      || current.prevHash !== previous.selfHash
    ) {
      fail('stock_command_ack_event_chain_mismatch');
    }
  }
}

function expectedFoodWritebackIds(
  envelope: StockCommandEnvelopeV1,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const event of envelope.events) {
    const payload = event.payload;
    const triggerUpdatesActiveLot = (
      payload.lot_action === 'updated_active'
      && (
        envelope.command.movement_class === 'receive'
        || envelope.command.movement_class === 'lot_state'
        || envelope.command.movement_class === 'correction'
      )
    );
    const triggerUpdatesPurchaseCost = (
      envelope.command.economic_effect === 'purchase'
      && typeof payload.unit_cost === 'number'
    );
    const foodId = payload.food_id;
    if (
      event.kind === 'stock_in'
      && payload.lot_closed !== true
      && (triggerUpdatesActiveLot || triggerUpdatesPurchaseCost)
      && typeof foodId === 'string'
      && !seen.has(foodId)
    ) {
      seen.add(foodId);
      ids.push(foodId);
    }
  }
  return ids;
}

function validateFoods(
  envelope: StockCommandEnvelopeV1,
  values: unknown[],
): void {
  const expectedIds = expectedFoodWritebackIds(envelope);
  const expected = new Set(expectedIds);
  const seen = new Set<string>();
  for (const value of values) {
    const food = requireRecord(value, 'stock_command_ack_food_invalid');
    const id = requireUuid(food.id, 'stock_command_ack_food_id_invalid');
    if (seen.has(id)) fail('stock_command_ack_food_duplicate', id);
    seen.add(id);
    if (!expected.has(id)) fail('stock_command_ack_food_target_forbidden', id);
    if (food.tenant_id !== envelope.command.tenant_id) {
      fail('stock_command_ack_food_tenant_mismatch', id);
    }
    requireNonNegativeInteger(food.version, 'stock_command_ack_food_version_invalid');
  }
  for (const id of expectedIds) {
    if (!seen.has(id)) fail('stock_command_ack_food_missing', id);
  }
}

interface ValidatedShipment {
  row: JsonRecord;
  id: string;
}

interface CommandShipmentTarget {
  id: string | null;
  mode: 'none' | 'issued' | 'void';
}

function commandShipmentTarget(
  envelope: StockCommandEnvelopeV1,
  downstream: StockCommandAckDownstreamContextV1 | undefined,
): CommandShipmentTarget {
  const command = envelope.command;
  if (
    (command.movement_class === 'dispatch'
      || command.movement_class === 'transfer')
    && typeof command.shipment_id === 'string'
  ) {
    if (downstream !== undefined) {
      fail('stock_command_ack_downstream_context_mismatch');
    }
    return { id: command.shipment_id, mode: 'issued' };
  }
  if (downstream !== undefined) {
    if (
      command.movement_class !== 'reverse'
      || downstream.kind !== 'shipment_void'
      || downstream.shipment_id !== command.reverses_command_id
    ) {
      fail('stock_command_ack_downstream_context_mismatch');
    }
    return { id: downstream.shipment_id, mode: 'void' };
  }
  return { id: null, mode: 'none' };
}

function validateShipments(
  envelope: StockCommandEnvelopeV1,
  values: unknown[],
  target: CommandShipmentTarget,
): ValidatedShipment[] {
  if (values.length > 1) {
    fail('stock_command_ack_shipment_cardinality_mismatch');
  }
  if (target.mode === 'none' && values.length !== 0) {
    fail('stock_command_ack_shipment_target_forbidden');
  }
  if (target.mode !== 'none' && values.length !== 1) {
    fail('stock_command_ack_shipment_missing', target.id ?? undefined);
  }

  return values.map((value) => {
    const shipment = requireRecord(
      value,
      'stock_command_ack_shipment_invalid',
    );
    const id = requireUuid(
      shipment.id,
      'stock_command_ack_shipment_id_invalid',
    );
    if (id !== target.id) {
      fail('stock_command_ack_shipment_target_forbidden', id);
    }
    if (shipment.tenant_id !== envelope.command.tenant_id) {
      fail('stock_command_ack_shipment_tenant_mismatch', id);
    }
    requireNonNegativeInteger(
      shipment.version,
      'stock_command_ack_shipment_version_invalid',
    );
    const expectedBusinessId = target.mode === 'issued'
      ? envelope.command.movement_class === 'transfer'
        ? envelope.command.from_business_id
        : envelope.command.movement_class === 'dispatch'
          ? envelope.command.business_id
          : null
      : target.mode === 'void' && envelope.command.movement_class === 'reverse'
        ? uniqueReverseShipmentBusinessId(envelope)
        : null;
    if (
      expectedBusinessId === null
      || shipment.business_id !== expectedBusinessId
    ) {
      fail('stock_command_ack_shipment_business_mismatch', id);
    }
    if (
      target.mode === 'issued'
      && (
        shipment.status !== 'issued'
        || id !== envelope.command.command_id
      )
    ) {
      fail('stock_command_ack_shipment_issue_binding_mismatch', id);
    }
    if (
      target.mode === 'void'
      && (
        shipment.status !== 'void'
        || shipment.stock_void_command_id !== envelope.command.command_id
      )
    ) {
      fail('stock_command_ack_shipment_void_binding_mismatch', id);
    }
    return { row: shipment, id };
  });
}

function uniqueReverseShipmentBusinessId(
  envelope: StockCommandEnvelopeV1,
): string | null {
  if (envelope.command.movement_class !== 'reverse') return null;
  const sourceBusinesses = new Set(
    envelope.command.movements
      .filter((movement) => movement.kind === 'stock_out')
      .map((movement) => movement.business_id),
  );
  return sourceBusinesses.size === 1
    ? [...sourceBusinesses][0]
    : null;
}

function validateAuditEvents(
  envelope: StockCommandEnvelopeV1,
  values: unknown[],
  commandEvents: readonly ValidatedServerEvent[],
  shipments: readonly ValidatedShipment[],
  target: CommandShipmentTarget,
): ValidatedServerEvent[] {
  const expectedKind = target.mode === 'issued'
    ? 'shipment_dispatched'
    : target.mode === 'void'
      ? 'shipment_voided'
      : null;
  const expectedCount = expectedKind === null ? 0 : 1;
  if (values.length !== expectedCount) {
    fail('stock_command_ack_audit_event_cardinality_mismatch');
  }

  const commandClientUuids = new Set(
    commandEvents.map((event) => event.clientUuid),
  );
  const seen = new Set<string>();
  return values.map((value) => {
    const audit = validateServerEventShape(
      value,
      AUDIT_EVENT_KEYS,
      'stock_command_ack_audit_event',
    );
    if (seen.has(audit.clientUuid) || commandClientUuids.has(audit.clientUuid)) {
      fail('stock_command_ack_audit_event_duplicate', audit.clientUuid);
    }
    seen.add(audit.clientUuid);
    if (audit.row.tenant_id !== envelope.command.tenant_id) {
      fail('stock_command_ack_audit_event_tenant_mismatch', audit.clientUuid);
    }
    if (audit.row.kind !== expectedKind) {
      fail('stock_command_ack_audit_event_kind_forbidden', audit.clientUuid);
    }
    if (audit.row.device_id !== envelope.device_id) {
      fail('stock_command_ack_audit_event_device_mismatch', audit.clientUuid);
    }
    const payload = requireRecord(
      audit.row.payload,
      'stock_command_ack_audit_event_payload_invalid',
    );
    if (
      payload.shipment_id !== target.id
      || shipments[0]?.id !== target.id
      || audit.row.business_id !== shipments[0]?.row.business_id
    ) {
      fail('stock_command_ack_audit_event_shipment_binding_mismatch');
    }
    if (
      expectedKind === 'shipment_voided'
      && (
        payload.stock_void_command_id !== envelope.command.command_id
        || payload.reason !== envelope.command.reason
      )
    ) {
      fail('stock_command_ack_audit_event_void_binding_mismatch');
    }
    return audit;
  });
}

function validateMasters(
  envelope: StockCommandEnvelopeV1,
  values: unknown[],
): void {
  const expectedTargets = new Map<string, number>();
  for (const [index, effect] of envelope.master_effects.entries()) {
    const key = `${effect.table}:${effect.id}`;
    if (expectedTargets.has(key)) {
      fail('stock_command_ack_expected_master_duplicate', key);
    }
    expectedTargets.set(key, index);
  }
  const seen = new Set<string>();
  values.forEach((value, index) => {
    const master = requireRecord(value, 'stock_command_ack_master_invalid');
    requireExactKeys(master, MASTER_KEYS, 'stock_command_ack_master_shape_invalid');
    if (typeof master.table !== 'string') {
      fail('stock_command_ack_master_table_invalid');
    }
    const id = requireUuid(master.id, 'stock_command_ack_master_id_invalid');
    const key = `${master.table}:${id}`;
    if (seen.has(key)) fail('stock_command_ack_master_duplicate', key);
    seen.add(key);
    if (expectedTargets.get(key) !== index) {
      fail('stock_command_ack_master_target_forbidden', key);
    }
    const row = requireRecord(master.row, 'stock_command_ack_master_row_invalid');
    if (row.id !== id) {
      fail('stock_command_ack_master_row_id_mismatch', key);
    }
    if (row.tenant_id !== envelope.command.tenant_id) {
      fail('stock_command_ack_master_tenant_mismatch', key);
    }
    const version = requireNonNegativeInteger(
      master.version,
      'stock_command_ack_master_version_invalid',
    );
    if (row.version !== version) {
      fail('stock_command_ack_master_row_version_mismatch', key);
    }
    const effect = envelope.master_effects[index];
    if (
      effect
      && Object.prototype.hasOwnProperty.call(effect.patch, 'business_id')
      && row.business_id !== effect.patch.business_id
    ) {
      fail('stock_command_ack_master_business_mismatch', key);
    }
  });
  for (const key of expectedTargets.keys()) {
    if (!seen.has(key)) fail('stock_command_ack_master_missing', key);
  }
  if (values.length !== envelope.master_effects.length) {
    fail('stock_command_ack_master_cardinality_mismatch');
  }
}

const SHIPMENT_ISSUE_RESULT_KEYS = [
  'doc_number',
  'doc_year',
  'shipment',
  'audit_event',
  'replayed',
  'writeback',
] as const;

const SHIPMENT_VOID_RESULT_KEYS = [
  'status',
  'shipment_id',
  'doc_number',
  'doc_year',
  'version',
  'replayed',
  'shipment',
  'audit_event',
  'writeback',
] as const;

const LABEL_OUTCOME_RESULT_KEYS = [
  'schema',
  'version',
  'intent_id',
  'outcome',
  'replayed',
  'writeback',
] as const;

interface ParsedWriteback {
  events: unknown[];
  auditEvents: unknown[];
  foods: unknown[];
  shipments: unknown[];
  masters: unknown[];
}

function parseStrictWriteback(
  value: unknown,
  codePrefix: string,
): ParsedWriteback {
  const writeback = requireRecord(value, `${codePrefix}_writeback_invalid`);
  requireExactKeys(
    writeback,
    WRITEBACK_KEYS,
    `${codePrefix}_writeback_shape_invalid`,
  );
  return {
    events: requireArray(
      writeback.events,
      `${codePrefix}_events_invalid`,
    ),
    auditEvents: requireArray(
      writeback.audit_events,
      `${codePrefix}_audit_events_invalid`,
    ),
    foods: requireArray(
      writeback.foods,
      `${codePrefix}_foods_invalid`,
    ),
    shipments: requireArray(
      writeback.shipments,
      `${codePrefix}_shipments_invalid`,
    ),
    masters: requireArray(
      writeback.masters,
      `${codePrefix}_masters_invalid`,
    ),
  };
}

function requireEmptyWritebackArray(
  values: unknown[],
  code: string,
): void {
  if (values.length !== 0) fail(code);
}

interface BoundShipmentRow {
  row: JsonRecord;
  businessId: string;
  version: number;
}

function validateBoundShipmentRow(
  value: unknown,
  input: {
    tenantId: string;
    shipmentId: string;
    status: 'issued' | 'void';
    codePrefix: string;
  },
): BoundShipmentRow {
  const row = requireRecord(value, `${input.codePrefix}_shipment_invalid`);
  if (row.id !== input.shipmentId) {
    fail(`${input.codePrefix}_shipment_id_mismatch`);
  }
  if (row.tenant_id !== input.tenantId) {
    fail(`${input.codePrefix}_shipment_tenant_mismatch`);
  }
  const businessId = requireUuid(
    row.business_id,
    `${input.codePrefix}_shipment_business_invalid`,
  );
  if (row.status !== input.status) {
    fail(`${input.codePrefix}_shipment_status_mismatch`);
  }
  const version = requireNonNegativeInteger(
    row.version,
    `${input.codePrefix}_shipment_version_invalid`,
  );
  return { row, businessId, version };
}

function validateMirroredShipmentAudit(
  value: unknown,
  input: {
    tenantId: string;
    shipmentId: string;
    businessId: string;
    kind: 'shipment_dispatched' | 'shipment_voided';
    deviceId: string;
    reason?: string;
    codePrefix: string;
  },
): ValidatedServerEvent {
  const audit = validateServerEventShape(
    value,
    AUDIT_EVENT_KEYS,
    `${input.codePrefix}_audit_event`,
  );
  if (audit.row.tenant_id !== input.tenantId) {
    fail(`${input.codePrefix}_audit_event_tenant_mismatch`);
  }
  if (audit.row.business_id !== input.businessId) {
    fail(`${input.codePrefix}_audit_event_business_mismatch`);
  }
  if (audit.row.kind !== input.kind) {
    fail(`${input.codePrefix}_audit_event_kind_mismatch`);
  }
  if (audit.row.device_id !== input.deviceId) {
    fail(`${input.codePrefix}_audit_event_device_mismatch`);
  }
  const payload = requireRecord(
    audit.row.payload,
    `${input.codePrefix}_audit_event_payload_invalid`,
  );
  if (payload.shipment_id !== input.shipmentId) {
    fail(`${input.codePrefix}_audit_event_shipment_mismatch`);
  }
  if (
    input.kind === 'shipment_voided'
    && (
      payload.reason !== input.reason
      || payload.stock_void_command_id !== null
    )
  ) {
    fail(`${input.codePrefix}_audit_event_void_binding_mismatch`);
  }
  return audit;
}

export interface ShipmentWithoutStockIssueAckValidationInput {
  tenantId: string;
  shipmentId: string;
  deviceId: string;
  result: unknown;
}

/**
 * Validates the status-only shipment issue RPC. Its writeback may contain only
 * the requested issued shipment and its matching shipment_dispatched audit.
 */
export function validateShipmentWithoutStockIssueWritebackV1({
  tenantId,
  shipmentId,
  deviceId,
  result,
}: ShipmentWithoutStockIssueAckValidationInput): JsonRecord {
  requireUuid(tenantId, 'shipment_no_stock_issue_expected_tenant_invalid');
  requireUuid(shipmentId, 'shipment_no_stock_issue_expected_id_invalid');
  if (deviceId.length === 0) {
    fail('shipment_no_stock_issue_expected_device_invalid');
  }
  const response = requireRecord(
    result,
    'shipment_no_stock_issue_result_invalid',
  );
  requireExactKeys(
    response,
    SHIPMENT_ISSUE_RESULT_KEYS,
    'shipment_no_stock_issue_result_shape_invalid',
  );
  const docNumber = requirePositiveInteger(
    response.doc_number,
    'shipment_no_stock_issue_doc_number_invalid',
  );
  const docYear = requirePositiveInteger(
    response.doc_year,
    'shipment_no_stock_issue_doc_year_invalid',
  );
  if (docYear < 2_000 || typeof response.replayed !== 'boolean') {
    fail('shipment_no_stock_issue_result_metadata_invalid');
  }

  const shipment = validateBoundShipmentRow(response.shipment, {
    tenantId,
    shipmentId,
    status: 'issued',
    codePrefix: 'shipment_no_stock_issue',
  });
  if (
    shipment.row.doc_number !== docNumber
    || shipment.row.doc_year !== docYear
  ) {
    fail('shipment_no_stock_issue_document_binding_mismatch');
  }
  const audit = validateMirroredShipmentAudit(response.audit_event, {
    tenantId,
    shipmentId,
    businessId: shipment.businessId,
    kind: 'shipment_dispatched',
    deviceId,
    codePrefix: 'shipment_no_stock_issue',
  });
  const payload = audit.row.payload as JsonRecord;
  if (
    payload.doc_number !== docNumber
    || payload.doc_year !== docYear
  ) {
    fail('shipment_no_stock_issue_audit_document_mismatch');
  }

  const writeback = parseStrictWriteback(
    response.writeback,
    'shipment_no_stock_issue',
  );
  requireEmptyWritebackArray(
    writeback.events,
    'shipment_no_stock_issue_events_forbidden',
  );
  requireEmptyWritebackArray(
    writeback.foods,
    'shipment_no_stock_issue_foods_forbidden',
  );
  requireEmptyWritebackArray(
    writeback.masters,
    'shipment_no_stock_issue_masters_forbidden',
  );
  if (
    writeback.shipments.length !== 1
    || !deepEqualJson(writeback.shipments[0], shipment.row)
  ) {
    fail('shipment_no_stock_issue_shipment_mirror_mismatch');
  }
  if (
    writeback.auditEvents.length !== 1
    || !deepEqualJson(writeback.auditEvents[0], audit.row)
  ) {
    fail('shipment_no_stock_issue_audit_mirror_mismatch');
  }
  return response;
}

export interface ShipmentWithoutStockVoidAckValidationInput {
  tenantId: string;
  shipmentId: string;
  reason: string;
  result: unknown;
}

/**
 * Validates the status-only shipment void RPC. No stock event, food, or master
 * is authorized; only the requested void shipment and shipment_voided audit.
 */
export function validateShipmentWithoutStockVoidWritebackV1({
  tenantId,
  shipmentId,
  reason: reasonInput,
  result,
}: ShipmentWithoutStockVoidAckValidationInput): JsonRecord {
  requireUuid(tenantId, 'shipment_no_stock_void_expected_tenant_invalid');
  requireUuid(shipmentId, 'shipment_no_stock_void_expected_id_invalid');
  const reason = reasonInput.trim();
  if (reason.length === 0 || reason !== reasonInput) {
    fail('shipment_no_stock_void_expected_reason_invalid');
  }
  const response = requireRecord(
    result,
    'shipment_no_stock_void_result_invalid',
  );
  requireExactKeys(
    response,
    SHIPMENT_VOID_RESULT_KEYS,
    'shipment_no_stock_void_result_shape_invalid',
  );
  if (
    response.status !== 'void'
    || response.shipment_id !== shipmentId
    || typeof response.replayed !== 'boolean'
  ) {
    fail('shipment_no_stock_void_result_identity_mismatch');
  }

  const shipment = validateBoundShipmentRow(response.shipment, {
    tenantId,
    shipmentId,
    status: 'void',
    codePrefix: 'shipment_no_stock_void',
  });
  if (shipment.row.stock_void_command_id !== null) {
    fail('shipment_no_stock_void_stock_binding_forbidden');
  }
  requireTimestamp(
    shipment.row.voided_at,
    'shipment_no_stock_void_voided_at_invalid',
  );
  if (
    response.version !== shipment.version
    || response.doc_number !== shipment.row.doc_number
    || response.doc_year !== shipment.row.doc_year
  ) {
    fail('shipment_no_stock_void_projection_mismatch');
  }
  if (
    response.doc_number !== null
    && (
      !Number.isSafeInteger(response.doc_number)
      || (response.doc_number as number) <= 0
    )
  ) {
    fail('shipment_no_stock_void_doc_number_invalid');
  }
  if (
    response.doc_year !== null
    && (
      !Number.isSafeInteger(response.doc_year)
      || (response.doc_year as number) < 2_000
    )
  ) {
    fail('shipment_no_stock_void_doc_year_invalid');
  }

  const audit = validateMirroredShipmentAudit(response.audit_event, {
    tenantId,
    shipmentId,
    businessId: shipment.businessId,
    kind: 'shipment_voided',
    deviceId: 'shipment-void-without-stock-v1',
    reason,
    codePrefix: 'shipment_no_stock_void',
  });
  const payload = audit.row.payload as JsonRecord;
  if (
    payload.doc_number !== response.doc_number
    || payload.doc_year !== response.doc_year
    || !sameInstant(
      requireTimestamp(
        payload.voided_at,
        'shipment_no_stock_void_audit_voided_at_invalid',
      ),
      shipment.row.voided_at as string,
    )
  ) {
    fail('shipment_no_stock_void_audit_projection_mismatch');
  }

  const writeback = parseStrictWriteback(
    response.writeback,
    'shipment_no_stock_void',
  );
  requireEmptyWritebackArray(
    writeback.events,
    'shipment_no_stock_void_events_forbidden',
  );
  requireEmptyWritebackArray(
    writeback.foods,
    'shipment_no_stock_void_foods_forbidden',
  );
  requireEmptyWritebackArray(
    writeback.masters,
    'shipment_no_stock_void_masters_forbidden',
  );
  if (
    writeback.shipments.length !== 1
    || !deepEqualJson(writeback.shipments[0], shipment.row)
  ) {
    fail('shipment_no_stock_void_shipment_mirror_mismatch');
  }
  if (
    writeback.auditEvents.length !== 1
    || !deepEqualJson(writeback.auditEvents[0], audit.row)
  ) {
    fail('shipment_no_stock_void_audit_mirror_mismatch');
  }
  return response;
}

export type ProductionLabelTerminalOutcomeV1 =
  | 'printed'
  | 'failed'
  | 'voided';

export interface ProductionLabelOutcomePendingBindingV1 {
  intent_id: string;
  tenant_id: string;
  business_id: string;
  production_id: string;
  command_id: string;
  outcome: ProductionLabelTerminalOutcomeV1;
  occurred_at: string;
  error: string | null;
  intent_snapshot: {
    id: string;
    tenant_id: string;
    business_id: string;
    production_id: string;
    command_id: string;
    audit_client_uuid: string;
    audit_kind: 'label_printed' | 'label_voided';
    label_payload: JsonRecord;
  };
}

export interface ProductionLabelOutcomeAckValidationInput {
  pending: ProductionLabelOutcomePendingBindingV1;
  deviceId: string;
  result: unknown;
}

/**
 * Binds a label outcome writeback to the durable local intent snapshot.
 * Depending on outcome/audit kind, the only permitted writes are the exact
 * audit event (or none) and the single updated intent master.
 */
export function validateProductionLabelOutcomeWritebackV1({
  pending,
  deviceId,
  result,
}: ProductionLabelOutcomeAckValidationInput): JsonRecord {
  validatePendingLabelBinding(pending);
  if (deviceId.length === 0) {
    fail('production_label_outcome_expected_device_invalid');
  }
  const response = requireRecord(
    result,
    'production_label_outcome_result_invalid',
  );
  requireExactKeys(
    response,
    LABEL_OUTCOME_RESULT_KEYS,
    'production_label_outcome_result_shape_invalid',
  );
  if (
    response.schema !== 'trackeat.production-label-print-outcome'
    || response.version !== 1
    || response.intent_id !== pending.intent_id
    || response.outcome !== pending.outcome
    || typeof response.replayed !== 'boolean'
  ) {
    fail('production_label_outcome_result_identity_mismatch');
  }
  const writeback = parseStrictWriteback(
    response.writeback,
    'production_label_outcome',
  );
  requireEmptyWritebackArray(
    writeback.events,
    'production_label_outcome_events_forbidden',
  );
  requireEmptyWritebackArray(
    writeback.foods,
    'production_label_outcome_foods_forbidden',
  );
  requireEmptyWritebackArray(
    writeback.shipments,
    'production_label_outcome_shipments_forbidden',
  );
  validateProductionLabelOutcomeMaster(pending, writeback.masters);
  validateProductionLabelOutcomeAudit(
    pending,
    deviceId,
    writeback.auditEvents,
  );
  return response;
}

function validatePendingLabelBinding(
  pending: ProductionLabelOutcomePendingBindingV1,
): void {
  const snapshot = pending.intent_snapshot;
  requireUuid(pending.intent_id, 'production_label_outcome_expected_intent_invalid');
  requireUuid(pending.tenant_id, 'production_label_outcome_expected_tenant_invalid');
  requireUuid(pending.business_id, 'production_label_outcome_expected_business_invalid');
  requireUuid(
    pending.production_id,
    'production_label_outcome_expected_production_invalid',
  );
  requireUuid(pending.command_id, 'production_label_outcome_expected_command_invalid');
  requireTimestamp(
    pending.occurred_at,
    'production_label_outcome_expected_occurred_at_invalid',
  );
  if (
    snapshot.id !== pending.intent_id
    || snapshot.tenant_id !== pending.tenant_id
    || snapshot.business_id !== pending.business_id
    || snapshot.production_id !== pending.production_id
    || snapshot.command_id !== pending.command_id
  ) {
    fail('production_label_outcome_expected_snapshot_mismatch');
  }
  requireUuid(
    snapshot.audit_client_uuid,
    'production_label_outcome_expected_audit_client_uuid_invalid',
  );
  const outcomeMatchesKind = (
    (
      snapshot.audit_kind === 'label_printed'
      && (pending.outcome === 'printed' || pending.outcome === 'failed')
    )
    || (
      snapshot.audit_kind === 'label_voided'
      && (pending.outcome === 'voided' || pending.outcome === 'failed')
    )
  );
  if (!outcomeMatchesKind) {
    fail('production_label_outcome_expected_kind_mismatch');
  }
  if (
    (pending.outcome === 'failed')
    !== (typeof pending.error === 'string' && pending.error.length > 0)
  ) {
    fail('production_label_outcome_expected_error_mismatch');
  }
}

function validateProductionLabelOutcomeMaster(
  pending: ProductionLabelOutcomePendingBindingV1,
  values: unknown[],
): void {
  if (values.length !== 1) {
    fail('production_label_outcome_master_cardinality_mismatch');
  }
  const master = requireRecord(
    values[0],
    'production_label_outcome_master_invalid',
  );
  requireExactKeys(
    master,
    MASTER_KEYS,
    'production_label_outcome_master_shape_invalid',
  );
  if (
    master.table !== 'production_label_print_intents_v1'
    || master.id !== pending.intent_id
  ) {
    fail('production_label_outcome_master_target_mismatch');
  }
  const version = requireNonNegativeInteger(
    master.version,
    'production_label_outcome_master_version_invalid',
  );
  const row = requireRecord(
    master.row,
    'production_label_outcome_master_row_invalid',
  );
  const snapshot = pending.intent_snapshot;
  if (
    row.id !== pending.intent_id
    || row.tenant_id !== pending.tenant_id
    || row.business_id !== pending.business_id
    || row.production_id !== pending.production_id
    || row.command_id !== pending.command_id
    || row.audit_client_uuid !== snapshot.audit_client_uuid
    || row.audit_kind !== snapshot.audit_kind
    || !deepEqualJson(row.label_payload, snapshot.label_payload)
    || row.state !== pending.outcome
    || row.error !== pending.error
    || row.version !== version
  ) {
    fail('production_label_outcome_master_binding_mismatch');
  }
  const resolvedAt = requireTimestamp(
    row.resolved_at,
    'production_label_outcome_master_resolved_at_invalid',
  );
  if (!sameInstant(resolvedAt, pending.occurred_at)) {
    fail('production_label_outcome_master_resolved_at_mismatch');
  }
}

function validateProductionLabelOutcomeAudit(
  pending: ProductionLabelOutcomePendingBindingV1,
  deviceId: string,
  values: unknown[],
): void {
  const snapshot = pending.intent_snapshot;
  const expectsAudit = (
    pending.outcome === 'printed'
    || (
      pending.outcome === 'voided'
      && snapshot.audit_kind === 'label_voided'
    )
  );
  if (values.length !== (expectsAudit ? 1 : 0)) {
    fail('production_label_outcome_audit_cardinality_mismatch');
  }
  if (!expectsAudit) return;

  const audit = validateServerEventShape(
    values[0],
    AUDIT_EVENT_KEYS,
    'production_label_outcome_audit_event',
  );
  if (
    audit.row.tenant_id !== pending.tenant_id
    || audit.row.business_id !== pending.business_id
    || audit.row.kind !== snapshot.audit_kind
    || audit.row.device_id !== deviceId
    || audit.row.client_uuid !== snapshot.audit_client_uuid
  ) {
    fail('production_label_outcome_audit_binding_mismatch');
  }
  if (
    !sameInstant(audit.row.client_ts as string, pending.occurred_at)
    || !serverSignedPayloadMatches(audit.row.payload, snapshot.label_payload)
  ) {
    fail('production_label_outcome_audit_payload_mismatch');
  }
}

/** The authenticated server adds the immutable operator signature at insert. */
export function serverSignedPayloadMatches(actual: unknown, expected: JsonRecord): boolean {
  if (!actual || typeof actual !== 'object' || Array.isArray(actual)) return false;
  if (Object.hasOwn(expected, '_operator')) return deepEqualJson(actual, expected);
  const { _operator: operator, ...label } = actual as JsonRecord;
  if (operator !== undefined) {
    if (!operator || typeof operator !== 'object' || Array.isArray(operator)) return false;
    const signature = operator as JsonRecord;
    const keys = ['scheme', 'grant_id', 'tenant_personnel_id', 'full_name', 'role', 'identity_fingerprint'];
    if (
      Object.keys(signature).length !== keys.length
      || keys.some((key) => !Object.hasOwn(signature, key))
      || signature.scheme !== 'trackeat-shared-tablet-operator-v1'
      || !UUID_PATTERN.test(String(signature.grant_id))
      || !UUID_PATTERN.test(String(signature.tenant_personnel_id))
      || typeof signature.full_name !== 'string' || !signature.full_name.trim()
      || typeof signature.role !== 'string' || !signature.role.trim()
      || !HASH_PATTERN.test(String(signature.identity_fingerprint))
    ) return false;
  }
  return deepEqualJson(label, expected);
}

/** JSON numbers entering PostgreSQL numeric are decimal strings, not doubles. */
function decimalProduct(left: number, right: number): number {
  const parts = (value: number): [bigint, number] => {
    const [mantissa, exponent = '0'] = String(value).split('e');
    const decimals = mantissa.split('.')[1]?.length ?? 0;
    return [BigInt(mantissa.replace('.', '')), Number(exponent) - decimals];
  };
  const [a, ae] = parts(left);
  const [b, be] = parts(right);
  return Number(`${a * b}e${ae + be}`);
}
